import * as THREE from 'three';
import {
  t,
  type SectorCoord,
  sectorKey,
  sectorName,
  sectorHasWarpGate,
  getZoneDifficulty,
  ZONE_TIER_KEYS,
  summarizeSectorNPCs,
  type SectorNPCSummary,
  SECTOR_SIZE,
  RACE_INFO,
  dominantRace,
  type SectorClaim,
  type ActiveSectorEvent,
} from '@viral-space/shared';
import { attachMobileCloseButton, isMobileLayout } from './ui-shared';

/**
 * Full-screen 3D hologram universe map shown when the player presses M.
 *
 * This is the planning / waypoint counterpart to the warp-gate UI:
 *
 * - The warp-gate UI is limited to *discovered gate sectors* and lets you
 *   pay credits to instantly warp.
 * - The universe map renders a volumetric slice of the infinite sector
 *   grid around a movable focus point so the pilot can plan long-range
 *   trips, mark a nav-beacon waypoint and see the danger gradient across
 *   unknown space.
 *
 * Both views share the same pop-up-hologram feel (drag to rotate, scroll
 * to zoom) so players immediately know how to interact with the second
 * map the first time they open it.
 *
 * Input:
 *   Drag            orbit the camera around the focus sector
 *   Scroll          zoom in / out
 *   Click a sector  select it (shows info in the right panel)
 *   Enter           confirm current selection as waypoint
 *   Arrows / WASD   pan the focus point one sector on the X / Z axis
 *   Q / E / PgUp /  move the focus up or down one Y layer
 *   PgDn
 *   Home            recenter focus on the player's current sector
 *   ESC / M         close
 *   Delete / Back   clear the active waypoint
 */
const COLORS = {
  primary: '#00ff41',
  primaryNum: 0x00ff41,
  secondary: '#ffb000',
  secondaryNum: 0xffb000,
  danger: '#ff4444',
  dangerNum: 0xff4444,
  dim: 'rgba(0,255,65,0.3)',
  dimSoft: 'rgba(0,255,65,0.15)',
  text: '#00ff41',
  bg: 'rgba(0,8,0,0.94)',
  panelBg: 'rgba(0,12,0,0.88)',
  waypoint: '#ff66cc',
  waypointNum: 0xff66cc,
  warpGate: '#bb77ff',
  warpGateNum: 0xbb77ff,
  /** Selection highlight — neutral so it never reads as “contested” (amber spheres). */
  selectionRingNum: 0xe8e8e8,
  // Zone tints keyed to difficulty (1-9). Each tier gets its own
  // colour so the danger gradient reads at a glance — green (safe) →
  // amber (contested) → orange (embattled) → purple (void) → deep
  // magenta (abyss) → blood red (nightmare) → hot pink (apex) → cyan
  // (singularity) → ice blue (transcendent) → white-gold (beyond).
  // Numbers are Three.js hex (for mesh colours) and the CSS strings
  // feed the info panel.
  zoneSafe: '#50c878',
  zoneSafeNum: 0x50c878,
  zoneContested: '#ffb000',
  zoneContestedNum: 0xffb000,
  zoneWar: '#ff8a1a',
  zoneWarNum: 0xff8a1a,
  zoneVoid: '#aa66ff',
  zoneVoidNum: 0xaa66ff,
  zoneAbyss: '#cc44ff',
  zoneAbyssNum: 0xcc44ff,
  zoneNightmare: '#ff2244',
  zoneNightmareNum: 0xff2244,
  zoneApex: '#ff44aa',
  zoneApexNum: 0xff44aa,
  zoneSingularity: '#22ddff',
  zoneSingularityNum: 0x22ddff,
  zoneTranscendent: '#aaeeff',
  zoneTranscendentNum: 0xaaeeff,
  zoneBeyond: '#fff7c0',
  zoneBeyondNum: 0xfff7c0,
  // Active sector event ring + badge tints. Bright orange so it never
  // collides with the green / amber / purple zone palette and reads
  // as "something is happening here right now". Severity-coloured
  // variants (warning / danger / success) keep the map honest about
  // how scary the event is — see eventRingColorNum().
  eventInfo: '#ffb000',
  eventInfoNum: 0xffb000,
  eventWarning: '#ff8a1a',
  eventWarningNum: 0xff8a1a,
  eventDanger: '#ff5050',
  eventDangerNum: 0xff5050,
  eventSuccess: '#3affb0',
  eventSuccessNum: 0x3affb0,
  // Galactic center marker — gold so it never collides with the green
  // (current) / amber (zone) / purple (warp gate) / magenta (waypoint)
  // / orange-red (event) palette. Reads as "the prime / origin sector"
  // at a glance.
  origin: '#ffd700',
  originNum: 0xffd700,
} as const;

const FONT = "'Share Tech Mono', monospace";
const GLOW = `0 0 8px ${COLORS.primary}, 0 0 16px rgba(0,255,65,0.3)`;

// Volume rendered around the focus point. ±4 on each axis → 9×9×9 = 729
// nodes, small enough to draw each as its own Mesh for trivial raycast
// picking and well within Three.js' comfort zone for one-time rebuilds
// when the focus moves. Bumped from ±3 (343) so distant pilots still
// see a meaningful slice of the universe and can locate landmarks like
// the galactic center without panning constantly.
const VIEW_RADIUS = 4;
// World-space spacing between sectors in the hologram. Picked to match
// the warp-gate UI's readability and give a clear sense of volume.
const SCALE = 2.5;

export interface UniverseMapOptions {
  onSetDestination: (sector: SectorCoord | null) => void;
  onClose: () => void;
}

interface ViewParams {
  current: SectorCoord;
  discovered: SectorCoord[];
  destination: SectorCoord | null;
  claims?: SectorClaim[];
  /** Currently-active world events (mothership alerts, raids, …). */
  activeEvents?: ActiveSectorEvent[];
}

export class UniverseMap {
  private container: HTMLElement;
  private root!: HTMLElement;
  private canvas!: HTMLCanvasElement;

  private infoTitleEl!: HTMLElement;
  private infoCoordEl!: HTMLElement;
  private infoOwnershipEl!: HTMLElement;
  private infoDetailEl!: HTMLElement;
  private infoDistanceEl!: HTMLElement;
  private infoZoneEl!: HTMLElement;
  private infoZoneDescEl!: HTMLElement;
  private infoNpcSummaryEl!: HTMLElement;
  private infoWarpGateEl!: HTMLElement;
  private infoHintEl!: HTMLElement;
  private setBtn!: HTMLButtonElement;
  private clearBtn!: HTMLButtonElement;
  private currentDestEl!: HTMLElement;
  private layerEl!: HTMLElement;
  private recenterBtn!: HTMLButtonElement;
  // Compass HUD: a tiny corner panel showing focus coords, distance to
  // the galactic center, and the cardinal axis the origin is in. Helps
  // pilots stay oriented when they pan far enough that (0,0,0) drops
  // off the volume.
  private compassEl!: HTMLElement;
  private compassFocusEl!: HTMLElement;
  private compassDistEl!: HTMLElement;
  private compassDirEl!: HTMLElement;

  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private volumeRoot: THREE.Group;
  private sectorMeshes: Map<string, THREE.Mesh> = new Map();
  // Destination line (current → waypoint), drawn in world space so it
  // survives camera orbit and stays anchored to both sectors.
  private destinationLine: THREE.Line | null = null;
  // Off-volume waypoint arrow shown when the destination falls outside
  // the ±VIEW_RADIUS cube around the focus — so the pilot can still see
  // which direction to pan.
  private destinationArrow: THREE.Line | null = null;
  private destinationArrowTip: THREE.Mesh | null = null;
  // Off-volume "where is the galactic center" arrow — only drawn when
  // (0,0,0) is outside the focus cube so a far-flung pilot still sees
  // which direction the origin lies relative to their current view.
  // Built as a thick cylinder (not a 1px Line) plus a cone tip plus
  // a "0,0,0" sprite label so it's actually visible against the
  // dark holo background even at default zoom.
  private originArrow: THREE.Mesh | null = null;
  private originArrowTip: THREE.Mesh | null = null;
  private originArrowLabel: THREE.Sprite | null = null;
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();

  private current: SectorCoord = { x: 0, y: 0, z: 0 };
  private focus: SectorCoord = { x: 0, y: 0, z: 0 };
  private discovered: Set<string> = new Set();
  private destination: SectorCoord | null = null;
  private selected: SectorCoord | null = null;
  private claimByKey = new Map<string, SectorClaim>();
  // Active world events grouped by sector key so render + info-panel
  // updates can look both up in O(1). The flat list is kept as well
  // because the info panel for a selected sector can show multiple
  // overlapping events.
  private eventsByKey = new Map<string, ActiveSectorEvent[]>();
  // Info-panel section for the active event (created lazily when the
  // selected sector has at least one event so we don't allocate DOM
  // for sectors that never see an event).
  private infoEventEl!: HTMLElement;

  private visible = false;
  private rafHandle = 0;
  private time = 0;

  private pointerDown: { x: number; y: number; time: number } | null = null;
  private dragging = false;
  // Live multi-touch tracking for pinch-to-zoom. We populate this on
  // pointerdown/move and drop entries on pointerup/cancel. When it
  // holds 2+ entries we suspend single-finger drag-to-rotate and
  // interpret the gesture as a pinch instead. Mobile-only; desktop
  // uses the wheel handler.
  private activePointers = new Map<number, { x: number; y: number }>();
  private pinchStartDist = 0;
  private pinchStartZoom = 1;
  // Camera orbit state: yaw, pitch, zoom. Initial values match the
  // warp-gate UI so the two 3D views feel like the same widget.
  private rotY = 0.35;
  private rotX = -0.45;
  private zoom = 1;

  private onSetDestinationCb: (s: SectorCoord | null) => void;
  private onCloseCb: () => void;

  private keyListener: ((e: KeyboardEvent) => void) | null = null;

  constructor(container: HTMLElement, opts: UniverseMapOptions) {
    this.container = container;
    this.onSetDestinationCb = opts.onSetDestination;
    this.onCloseCb = opts.onClose;
    this.scene = new THREE.Scene();
    this.volumeRoot = new THREE.Group();
    this.scene.add(this.volumeRoot);
    this.build();
  }

  private el(tag: string, styles: Partial<CSSStyleDeclaration>, text?: string): HTMLElement {
    const e = document.createElement(tag);
    Object.assign(e.style, styles);
    if (text !== undefined) e.textContent = text;
    return e;
  }

  private build(): void {
    this.root = this.el('div', {
      position: 'absolute',
      inset: '0',
      display: 'none',
      flexDirection: 'column',
      fontFamily: FONT,
      color: COLORS.primary,
      background: COLORS.bg,
      zIndex: '80',
      pointerEvents: 'auto',
      overflow: 'hidden',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    });
    this.container.appendChild(this.root);

    const header = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 24px',
      borderBottom: `1px solid ${COLORS.dim}`,
      flexShrink: '0',
    });

    const titleBlock = this.el('div', { display: 'flex', flexDirection: 'column', gap: '2px' });
    titleBlock.appendChild(this.el('div', {
      fontSize: 'clamp(14px, 2vw, 22px)',
      letterSpacing: '4px',
      textShadow: GLOW,
    }, t('universeMap.title')));
    titleBlock.appendChild(this.el('div', {
      fontSize: '10px',
      color: 'rgba(0,255,65,0.55)',
    }, t('universeMap.subtitle')));
    header.appendChild(titleBlock);

    const closeBtn = document.createElement('button');
    Object.assign(closeBtn.style, {
      fontFamily: FONT,
      fontSize: 'clamp(11px, 1.3vw, 14px)',
      padding: '6px 18px',
      background: 'transparent',
      border: `2px solid ${COLORS.danger}`,
      color: COLORS.danger,
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: '3px',
      textShadow: `0 0 6px ${COLORS.danger}`,
      transition: 'all 0.2s',
    });
    closeBtn.textContent = t('universeMap.close');
    closeBtn.addEventListener('mouseenter', () => {
      closeBtn.style.background = COLORS.danger;
      closeBtn.style.color = '#000';
    });
    closeBtn.addEventListener('mouseleave', () => {
      closeBtn.style.background = 'transparent';
      closeBtn.style.color = COLORS.danger;
    });
    closeBtn.addEventListener('click', () => {
      this.hide();
      this.onCloseCb();
    });
    header.appendChild(closeBtn);
    this.root.appendChild(header);

    // Hide the header CLOSE button on mobile so it doesn't overlap the
    // corner ✕ that `attachMobileCloseButton` paints on the top-right.
    attachMobileCloseButton(
      this.root,
      () => {
        this.hide();
        this.onCloseCb();
      },
      { hideOnMobile: [closeBtn] },
    );

    const body = this.el('div', {
      display: 'flex',
      flex: '1',
      minHeight: '0',
      position: 'relative',
    });
    this.root.appendChild(body);

    this.canvas = document.createElement('canvas');
    Object.assign(this.canvas.style, {
      flex: '1',
      display: 'block',
      cursor: 'grab',
      touchAction: 'none',
    });
    body.appendChild(this.canvas);

    this.buildLayerControls(body);
    this.buildCompass(body);
    this.buildInfoPanel(body);
    this.attachCanvasEvents();
  }

  /**
   * "Where am I, where is the galactic center" overlay anchored to the
   * top-left of the canvas. Designed to be impossible to miss: a
   * gold-bordered card that always shows the focus coordinates, the
   * straight-line distance back to (0,0,0), and a coarse axis hint
   * (e.g. "←X +Y −Z") so the pilot can pan toward home even when the
   * gold beacon is far outside the rendered volume.
   *
   * Repainted via {@link updateCompass} on every focus change.
   */
  private buildCompass(parent: HTMLElement): void {
    const mobile = isMobileLayout();
    this.compassEl = this.el('div', {
      position: 'absolute',
      top: mobile ? '8px' : '16px',
      left: mobile ? '8px' : '16px',
      background: 'rgba(0,12,0,0.94)',
      border: `2px solid ${COLORS.origin}`,
      boxShadow: `0 0 10px rgba(255,215,0,0.35), inset 0 0 6px rgba(255,215,0,0.08)`,
      padding: mobile ? '8px 10px' : '10px 14px',
      fontSize: mobile ? '9px' : '11px',
      color: COLORS.primary,
      pointerEvents: 'none',
      letterSpacing: '1px',
      zIndex: '2',
      display: 'flex',
      flexDirection: 'column',
      gap: mobile ? '4px' : '5px',
      minWidth: mobile ? '128px' : '180px',
    });
    parent.appendChild(this.compassEl);

    this.compassEl.appendChild(
      this.el('div', {
        fontSize: mobile ? '8px' : '9px',
        color: 'rgba(0,255,65,0.55)',
        letterSpacing: mobile ? '2px' : '3px',
      }, t('universeMap.compassFocus')),
    );
    this.compassFocusEl = this.el('div', {
      fontSize: mobile ? '12px' : '14px',
      textShadow: GLOW,
      letterSpacing: '2px',
    }, '');
    this.compassEl.appendChild(this.compassFocusEl);

    this.compassEl.appendChild(this.el('div', {
      height: '1px',
      background: 'rgba(255,215,0,0.35)',
      margin: '2px 0',
    }));

    this.compassEl.appendChild(
      this.el('div', {
        fontSize: mobile ? '8px' : '9px',
        color: 'rgba(255,215,0,0.7)',
        letterSpacing: mobile ? '2px' : '3px',
      }, t('universeMap.compassDirection')),
    );

    this.compassDistEl = this.el('div', {
      fontSize: mobile ? '11px' : '13px',
      color: COLORS.origin,
      textShadow: `0 0 6px rgba(255,215,0,0.55)`,
      letterSpacing: '2px',
    }, '');
    this.compassEl.appendChild(this.compassDistEl);

    this.compassDirEl = this.el('div', {
      fontSize: mobile ? '12px' : '15px',
      color: COLORS.origin,
      textShadow: `0 0 6px rgba(255,215,0,0.5)`,
      letterSpacing: '4px',
      fontWeight: 'bold',
    }, '');
    this.compassEl.appendChild(this.compassDirEl);
  }

  private updateCompass(): void {
    const f = this.focus;
    this.compassFocusEl.textContent = `[${f.x}, ${f.y}, ${f.z}]`;
    const dist = Math.round(
      Math.sqrt(f.x * f.x + f.y * f.y + f.z * f.z) * 100,
    ) / 100;
    if (dist === 0) {
      this.compassDistEl.textContent = t('universeMap.compassAtOrigin');
      this.compassDirEl.textContent = '★';
      return;
    }
    this.compassDistEl.textContent = t('universeMap.compassDistance', {
      n: dist.toString(),
    });
    // Direction hint with arrow glyphs so it reads at a glance:
    // "←X +Y −Z" means "pan focus −X / +Y / −Z to reach (0,0,0)".
    // A solid arrow on the dominant axis emphasises the cardinal
    // direction so the pilot doesn't have to mentally parse three
    // signs to figure out where to go.
    const ax = Math.abs(f.x), ay = Math.abs(f.y), az = Math.abs(f.z);
    const dom = Math.max(ax, ay, az);
    const partX = f.x === 0 ? '' : `${ax === dom ? (f.x > 0 ? '←X' : '→X') : (f.x > 0 ? '−X' : '+X')}`;
    const partY = f.y === 0 ? '' : `${ay === dom ? (f.y > 0 ? '↓Y' : '↑Y') : (f.y > 0 ? '−Y' : '+Y')}`;
    const partZ = f.z === 0 ? '' : `${az === dom ? (f.z > 0 ? '←Z' : '→Z') : (f.z > 0 ? '−Z' : '+Z')}`;
    this.compassDirEl.textContent = [partX, partY, partZ].filter(Boolean).join(' ');
  }

  private buildLayerControls(parent: HTMLElement): void {
    // Mobile branch: the bar previously hugged a single horizontal row
    // of tiny 3-px-padding buttons that overflowed off-screen in a
    // 430×180 landscape phone once we added the new CUSTOM control.
    // We let the row wrap and bump touch targets up to a comfortable
    // 28px-tall hit area so a thumb can find them.
    const mobile = isMobileLayout();
    const bar = this.el('div', {
      position: 'absolute',
      bottom: mobile ? '8px' : '18px',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: mobile ? '6px' : '10px',
      maxWidth: '95vw',
      background: COLORS.panelBg,
      border: `1px solid ${COLORS.dim}`,
      padding: mobile ? '6px 8px' : '6px 12px',
      fontSize: mobile ? '10px' : '11px',
      pointerEvents: 'auto',
      zIndex: '2',
    });
    parent.appendChild(bar);

    const makeBtn = (label: string, title: string, onClick: () => void): HTMLButtonElement => {
      const b = document.createElement('button');
      Object.assign(b.style, {
        fontFamily: FONT,
        fontSize: mobile ? '11px' : '11px',
        // Bigger vertical padding on mobile so the button hits the
        // ~32px Apple/Google touch-target heuristic without
        // outgrowing the bar visually.
        padding: mobile ? '6px 10px' : '3px 10px',
        background: 'transparent',
        border: `1px solid ${COLORS.dim}`,
        color: COLORS.primary,
        cursor: 'pointer',
        letterSpacing: mobile ? '1px' : '2px',
        textShadow: GLOW,
        // Disable iOS double-tap zoom on these tightly-packed
        // buttons so a fast pilot can hammer Y+/Y- without zoom
        // gestures fighting the input.
        touchAction: 'manipulation',
      } as Partial<CSSStyleDeclaration>);
      b.textContent = label;
      b.title = title;
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        onClick();
      });
      return b;
    };

    bar.appendChild(makeBtn('Y−', t('universeMap.layerDown'), () => {
      this.focus = { ...this.focus, y: this.focus.y - 1 };
      this.onFocusChanged();
    }));

    this.layerEl = this.el('div', {
      // Shrinking the layer label on mobile keeps the bar from
      // forcing a wrap on the very first row — Y- / LAYER / Y+
      // should stay together so the pilot can step layers without
      // the buttons reflowing every tap.
      minWidth: mobile ? '110px' : '150px',
      textAlign: 'center',
      fontSize: mobile ? '11px' : '12px',
      letterSpacing: mobile ? '2px' : '3px',
      textShadow: GLOW,
    }, '');
    bar.appendChild(this.layerEl);

    bar.appendChild(makeBtn('Y+', t('universeMap.layerUp'), () => {
      this.focus = { ...this.focus, y: this.focus.y + 1 };
      this.onFocusChanged();
    }));

    this.recenterBtn = makeBtn(
      t('universeMap.recenter'),
      t('universeMap.recenterHint'),
      () => {
        this.focus = { ...this.current };
        this.onFocusChanged();
      },
    );
    this.recenterBtn.style.marginLeft = mobile ? '0' : '14px';
    this.recenterBtn.style.borderColor = COLORS.secondary;
    this.recenterBtn.style.color = COLORS.secondary;
    this.recenterBtn.style.textShadow = `0 0 6px ${COLORS.secondary}`;
    bar.appendChild(this.recenterBtn);

    // "Snap focus to (0,0,0)" — gives the pilot a one-click way to
    // jump straight to the galactic center even when they are dozens
    // of sectors away. Coloured gold to match the origin beacon /
    // compass styling so it reads as part of the same orientation
    // pattern, not as a separate feature.
    const originBtn = makeBtn(
      t('universeMap.focusOrigin'),
      t('universeMap.focusOriginHint'),
      () => {
        this.focus = { x: 0, y: 0, z: 0 };
        this.selected = { x: 0, y: 0, z: 0 };
        this.onFocusChanged();
      },
    );
    originBtn.style.marginLeft = '0';
    originBtn.style.borderColor = COLORS.origin;
    originBtn.style.color = COLORS.origin;
    originBtn.style.textShadow = `0 0 6px ${COLORS.origin}`;
    bar.appendChild(originBtn);

    // "Custom waypoint…" — opens a tiny X/Y/Z input dialog so a
    // pilot can type a sector by coordinate and skip the panning.
    // Coloured magenta to match the waypoint visual language used
    // elsewhere on the map (waypoint ring, dashed nav line).
    // Mobile uses a compact label so the bar doesn't wrap mid-row.
    const customBtn = makeBtn(
      mobile
        ? t('universeMap.customDestinationButtonShort')
        : t('universeMap.customDestinationButton'),
      t('universeMap.customDestinationHint'),
      () => this.openCustomDestinationDialog(),
    );
    customBtn.style.marginLeft = '0';
    customBtn.style.borderColor = COLORS.waypoint;
    customBtn.style.color = COLORS.waypoint;
    customBtn.style.textShadow = `0 0 6px ${COLORS.waypoint}`;
    bar.appendChild(customBtn);
  }

  /**
   * Modal-style overlay that lets the pilot punch in an absolute sector
   * coordinate (X / Y / Z) and treat it as a destination directly,
   * without panning the volumetric view to find it first. Useful for
   * deep-frontier targets dozens of sectors out where keyboard-panning
   * would take a while.
   *
   * Behaviour:
   *   - Inputs are `type="number"` with `step=1` so they accept positive
   *     and negative integers (HTML5 native validation rejects letters
   *     and decimals). We additionally `Math.trunc` on submit to clamp
   *     stray fractional input pasted from the clipboard.
   *   - Default values mirror the current focus so a tiny tweak (e.g.
   *     "+5 on Z") only requires typing one field.
   *   - Enter submits, Escape cancels.
   *   - On submit we recenter the focus + selection on the typed sector
   *     AND set it as the active waypoint, so the pilot immediately
   *     sees the magenta beacon land at the right spot.
   */
  private customDialogEl: HTMLElement | null = null;

  private openCustomDestinationDialog(): void {
    if (this.customDialogEl) return;

    // Mobile branch: bigger touch targets, 16px input font (so iOS
    // Safari doesn't auto-zoom into the page when the keyboard pops
    // open), and a card width that uses most of the screen so a
    // landscape phone doesn't end up with three 30-px-wide inputs
    // that are impossible to tap.
    const mobile = isMobileLayout();

    const overlay = this.el('div', {
      position: 'absolute',
      inset: '0',
      background: 'rgba(0,4,0,0.55)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: '5',
      pointerEvents: 'auto',
    });
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.closeCustomDestinationDialog();
    });

    const card = this.el('div', {
      background: COLORS.bg,
      border: `2px solid ${COLORS.waypoint}`,
      boxShadow: `0 0 14px rgba(255,102,204,0.45), inset 0 0 8px rgba(255,102,204,0.08)`,
      padding: mobile ? '14px 16px' : '18px 22px',
      display: 'flex',
      flexDirection: 'column',
      gap: mobile ? '8px' : '10px',
      minWidth: mobile ? 'min(320px, 92vw)' : '280px',
      maxWidth: mobile ? '92vw' : '90vw',
      // Keep the dialog inside the viewport with the soft keyboard
      // open. `min(...)` falls back to vh on browsers without dvh
      // support; either way the user never has to scroll the dialog
      // to reach the SET button.
      maxHeight: mobile ? 'min(70dvh, 70vh)' : '85vh',
      overflowY: 'auto',
      fontFamily: FONT,
      color: COLORS.primary,
      letterSpacing: '1px',
    });
    overlay.appendChild(card);

    card.appendChild(
      this.el('div', {
        fontSize: mobile ? '13px' : '14px',
        letterSpacing: mobile ? '2px' : '3px',
        color: COLORS.waypoint,
        textShadow: `0 0 6px ${COLORS.waypoint}`,
      }, t('universeMap.customDestinationTitle')),
    );

    card.appendChild(
      this.el('div', {
        fontSize: mobile ? '11px' : '10px',
        color: 'rgba(0,255,65,0.6)',
        letterSpacing: '1px',
        textTransform: 'none',
        lineHeight: '1.4',
      }, t('universeMap.customDestinationHelp')),
    );

    const fields = this.el('div', {
      display: 'flex',
      gap: mobile ? '8px' : '10px',
      justifyContent: 'space-between',
    });
    card.appendChild(fields);

    const inputs: HTMLInputElement[] = [];
    const labels: ('X' | 'Y' | 'Z')[] = ['X', 'Y', 'Z'];
    const defaults = [this.focus.x, this.focus.y, this.focus.z];
    for (let i = 0; i < 3; i++) {
      const wrap = this.el('label', {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        flex: '1',
        // Stops a long focus value (e.g. -12345) from making one
        // column blow up and squeeze the others on a narrow phone.
        minWidth: '0',
      });
      wrap.appendChild(
        this.el('span', {
          fontSize: mobile ? '11px' : '10px',
          letterSpacing: '2px',
          color: COLORS.waypoint,
        }, labels[i]!),
      );
      const input = document.createElement('input');
      input.type = 'number';
      input.step = '1';
      // No min/max — sector coords are an unbounded signed integer
      // grid. `step=1` plus the `Math.trunc` on submit clamp away
      // accidental decimal pastes; the negative sign is allowed by
      // `type="number"` natively.
      input.value = String(defaults[i]);
      // `decimal` (not `numeric`) is what surfaces the negative sign
      // and the minus key on most mobile keyboards. Without this, the
      // numpad on iOS hides the `-` and the pilot has no way to type
      // a negative sector coord.
      input.inputMode = 'decimal';
      // `enterkeyhint="go"` flips the on-screen keyboard's Return
      // key to a "Go / Submit" caption so the pilot understands they
      // can submit the form straight from the keyboard.
      input.setAttribute('enterkeyhint', 'go');
      Object.assign(input.style, {
        fontFamily: FONT,
        // 16px+ on mobile is the documented threshold below which
        // iOS Safari auto-zooms the viewport when the field gets
        // focus. Keeping the desktop value at 14 preserves the old
        // dense feel.
        fontSize: mobile ? '16px' : '14px',
        padding: mobile ? '10px 8px' : '6px 8px',
        background: 'rgba(0,12,0,0.85)',
        border: `1px solid ${COLORS.dim}`,
        color: COLORS.primary,
        letterSpacing: '2px',
        textAlign: 'center',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
        // Stop iOS double-tap zoom from eating fast taps between
        // fields.
        touchAction: 'manipulation',
      } as Partial<CSSStyleDeclaration>);
      input.addEventListener('focus', () => {
        input.style.borderColor = COLORS.waypoint;
        input.style.boxShadow = `0 0 6px ${COLORS.waypoint}`;
        input.select();
      });
      input.addEventListener('blur', () => {
        input.style.borderColor = COLORS.dim;
        input.style.boxShadow = '';
      });
      wrap.appendChild(input);
      fields.appendChild(wrap);
      inputs.push(input);
    }

    const actions = this.el('div', {
      display: 'flex',
      gap: mobile ? '10px' : '8px',
      justifyContent: mobile ? 'space-between' : 'flex-end',
      marginTop: mobile ? '6px' : '4px',
    });
    card.appendChild(actions);

    const cancelBtn = document.createElement('button');
    Object.assign(cancelBtn.style, {
      fontFamily: FONT,
      fontSize: mobile ? '12px' : '11px',
      // Mobile gets full-width-ish action buttons with a chunky
      // vertical padding so a thumb easily clears either choice.
      padding: mobile ? '10px 16px' : '6px 14px',
      flex: mobile ? '1' : '',
      background: 'transparent',
      border: `1px solid ${COLORS.dim}`,
      color: 'rgba(0,255,65,0.7)',
      cursor: 'pointer',
      letterSpacing: '2px',
      touchAction: 'manipulation',
    } as Partial<CSSStyleDeclaration>);
    cancelBtn.textContent = t('universeMap.customDestinationCancel');
    cancelBtn.addEventListener('click', () => this.closeCustomDestinationDialog());
    actions.appendChild(cancelBtn);

    const submitBtn = document.createElement('button');
    Object.assign(submitBtn.style, {
      fontFamily: FONT,
      fontSize: mobile ? '13px' : '12px',
      padding: mobile ? '10px 18px' : '6px 16px',
      flex: mobile ? '1' : '',
      background: 'transparent',
      border: `2px solid ${COLORS.waypoint}`,
      color: COLORS.waypoint,
      cursor: 'pointer',
      letterSpacing: '3px',
      textShadow: `0 0 6px ${COLORS.waypoint}`,
      touchAction: 'manipulation',
    } as Partial<CSSStyleDeclaration>);
    submitBtn.textContent = t('universeMap.customDestinationSet');
    const submit = (): void => {
      // `Math.trunc` covers the "user pasted '3.7' or '-2.9'" case
      // that `type="number"` would otherwise hand us as a float.
      // `Number.isFinite` filters NaN from an empty / invalid field.
      const raw = inputs.map((i) => Number(i.value));
      if (!raw.every((n) => Number.isFinite(n))) {
        // Mark the offending field(s) in danger red so the user knows
        // which one to fix instead of silently failing.
        for (let i = 0; i < inputs.length; i++) {
          if (!Number.isFinite(raw[i]!)) {
            inputs[i]!.style.borderColor = COLORS.danger;
            inputs[i]!.style.boxShadow = `0 0 6px ${COLORS.danger}`;
          }
        }
        return;
      }
      const [x, y, z] = raw.map((n) => Math.trunc(n));
      const sector: SectorCoord = { x: x!, y: y!, z: z! };
      this.focus = { ...sector };
      this.selected = { ...sector };
      this.destination = { ...sector };
      this.onSetDestinationCb({ ...sector });
      this.closeCustomDestinationDialog();
      // `onFocusChanged` rebuilds the volume which in turn rebuilds the
      // destination + origin arrows, so we don't need a second explicit
      // rebuildDestinationVisual() here.
      this.onFocusChanged();
    };
    submitBtn.addEventListener('click', submit);
    actions.appendChild(submitBtn);

    overlay.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        submit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.closeCustomDestinationDialog();
      }
    });

    this.root.appendChild(overlay);
    this.customDialogEl = overlay;
    // Focus the X field so keyboard-driven entry works without an
    // initial mouse click.
    setTimeout(() => inputs[0]!.focus(), 0);
  }

  private closeCustomDestinationDialog(): void {
    if (!this.customDialogEl) return;
    this.customDialogEl.remove();
    this.customDialogEl = null;
  }

  private buildInfoPanel(parent: HTMLElement): void {
    // On mobile landscape the side panel has to share ~700–900px with a
    // 3D canvas AND the bottom layer-control bar, so we cap its width at
    // ~40 % and squeeze padding/gap so it doesn't swallow the hologram.
    // Desktop keeps the roomy clamp it had before.
    const mobile = isMobileLayout();
    const panel = this.el('div', {
      position: 'absolute',
      top: mobile ? '8px' : '16px',
      right: mobile ? '8px' : '16px',
      width: mobile ? 'min(40vw, 220px)' : 'clamp(240px, 26vw, 340px)',
      background: COLORS.panelBg,
      border: `1px solid ${COLORS.dim}`,
      boxShadow: `0 0 12px rgba(0,255,65,0.18)`,
      padding: mobile ? '10px 12px' : '18px',
      display: 'flex',
      flexDirection: 'column',
      gap: mobile ? '6px' : '10px',
      pointerEvents: 'auto',
      zIndex: '2',
      // Leave room for both the mobile ✕ (top-right, ~42px high) and the
      // bottom layer-control bar (~48px). Without this the panel can
      // grow tall enough to hide the Y± controls on short landscape
      // viewports.
      maxHeight: mobile ? 'calc(100% - 72px)' : 'calc(100% - 32px)',
      fontSize: mobile ? '10px' : '',
      overflow: 'auto',
    });
    parent.appendChild(panel);

    panel.appendChild(this.el('div', {
      fontSize: mobile ? '8px' : '9px',
      color: 'rgba(0,255,65,0.55)',
      letterSpacing: mobile ? '2px' : '3px',
    }, t('universeMap.selected')));

    this.infoTitleEl = this.el('div', {
      fontSize: mobile ? '13px' : '18px',
      textShadow: GLOW,
      letterSpacing: mobile ? '1px' : '2px',
      minHeight: mobile ? '16px' : '22px',
    }, '—');
    panel.appendChild(this.infoTitleEl);

    this.infoCoordEl = this.el('div', {
      fontSize: mobile ? '9px' : '11px',
      color: 'rgba(0,255,65,0.55)',
      letterSpacing: mobile ? '2px' : '3px',
      minHeight: mobile ? '12px' : '14px',
    }, '');
    panel.appendChild(this.infoCoordEl);

    this.infoOwnershipEl = this.el('div', {
      fontSize: mobile ? '9px' : '10px',
      color: 'rgba(0,255,65,0.85)',
      letterSpacing: '1px',
      minHeight: mobile ? '12px' : '14px',
      textTransform: 'none',
    }, '');
    panel.appendChild(this.infoOwnershipEl);

    this.infoDetailEl = this.el('div', {
      fontSize: mobile ? '9px' : '10px',
      color: 'rgba(0,255,65,0.7)',
      minHeight: mobile ? '12px' : '14px',
      textTransform: 'none',
    }, '');
    panel.appendChild(this.infoDetailEl);

    // Active-event card lives between the description and the metric
    // strip. Hidden by default; shown when the selected sector has at
    // least one running event (mothership alert, raid, skirmish, …).
    this.infoEventEl = this.el('div', {
      display: 'none',
      flexDirection: 'column',
      gap: '4px',
      marginTop: '6px',
      padding: mobile ? '6px 8px' : '8px 10px',
      border: `1px solid ${COLORS.eventInfo}`,
      background: 'rgba(255,176,0,0.08)',
      boxShadow: `0 0 8px rgba(255,176,0,0.15)`,
      textTransform: 'none',
      letterSpacing: '0',
    });
    panel.appendChild(this.infoEventEl);

    panel.appendChild(this.el('div', {
      height: '1px',
      background: COLORS.dim,
      margin: '4px 0',
    }));

    const metricStyle: Partial<CSSStyleDeclaration> = {
      fontSize: mobile ? '9px' : '11px',
      letterSpacing: mobile ? '1px' : '2px',
    };
    this.infoZoneEl = this.el('div', metricStyle, '');
    panel.appendChild(this.infoZoneEl);

    // Tier flavour description — softer text than the metric line
    // above, no letter-spacing, wraps naturally so the player can
    // actually read the paragraph instead of a teletype banner.
    this.infoZoneDescEl = this.el('div', {
      fontSize: mobile ? '9px' : '10px',
      lineHeight: '1.45',
      letterSpacing: '0',
      color: 'rgba(0,255,65,0.65)',
      margin: '2px 0 2px',
      whiteSpace: 'normal',
    }, '');
    panel.appendChild(this.infoZoneDescEl);

    // Hostile/neutral NPC breakdown — sits below the zone block so a
    // pilot scanning the panel reads "tier label → tier flavour →
    // here's what's actually shooting at you in there".
    this.infoNpcSummaryEl = this.el('div', metricStyle, '');
    panel.appendChild(this.infoNpcSummaryEl);

    this.infoDistanceEl = this.el('div', metricStyle, '');
    panel.appendChild(this.infoDistanceEl);

    this.infoWarpGateEl = this.el('div', metricStyle, '');
    panel.appendChild(this.infoWarpGateEl);

    panel.appendChild(this.el('div', {
      height: '1px',
      background: COLORS.dim,
      margin: '4px 0',
    }));

    this.setBtn = document.createElement('button');
    Object.assign(this.setBtn.style, {
      fontFamily: FONT,
      fontSize: mobile ? '10px' : '13px',
      padding: mobile ? '7px 10px' : '10px 16px',
      background: 'transparent',
      border: `2px solid ${COLORS.waypoint}`,
      color: COLORS.waypoint,
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: mobile ? '2px' : '3px',
      textShadow: `0 0 6px ${COLORS.waypoint}`,
      transition: 'all 0.2s',
    });
    this.setBtn.textContent = t('universeMap.setDestination');
    this.setBtn.addEventListener('mouseenter', () => {
      if (this.setBtn.disabled) return;
      this.setBtn.style.background = COLORS.waypoint;
      this.setBtn.style.color = '#000';
    });
    this.setBtn.addEventListener('mouseleave', () => {
      this.setBtn.style.background = 'transparent';
      this.setBtn.style.color = COLORS.waypoint;
    });
    this.setBtn.addEventListener('click', () => {
      if (!this.selected || this.setBtn.disabled) return;
      const s = { ...this.selected };
      this.destination = s;
      this.onSetDestinationCb(s);
      this.rebuildDestinationVisual();
      this.updateInfoPanel();
    });
    panel.appendChild(this.setBtn);

    this.clearBtn = document.createElement('button');
    Object.assign(this.clearBtn.style, {
      fontFamily: FONT,
      fontSize: mobile ? '9px' : '11px',
      padding: mobile ? '5px 10px' : '6px 12px',
      background: 'transparent',
      border: `1px solid ${COLORS.dim}`,
      color: 'rgba(0,255,65,0.7)',
      cursor: 'pointer',
      letterSpacing: mobile ? '1px' : '2px',
    });
    this.clearBtn.textContent = t('universeMap.clearDestination');
    this.clearBtn.addEventListener('click', () => {
      this.destination = null;
      this.onSetDestinationCb(null);
      this.rebuildDestinationVisual();
      this.updateInfoPanel();
    });
    panel.appendChild(this.clearBtn);

    this.currentDestEl = this.el('div', {
      fontSize: mobile ? '9px' : '11px',
      color: COLORS.waypoint,
      textShadow: `0 0 6px ${COLORS.waypoint}`,
      marginTop: mobile ? '4px' : '6px',
      minHeight: mobile ? '12px' : '14px',
      letterSpacing: mobile ? '1px' : '2px',
    }, '');
    panel.appendChild(this.currentDestEl);

    panel.appendChild(this.el('div', {
      height: '1px',
      background: COLORS.dim,
      margin: '8px 0',
    }));

    this.infoHintEl = this.el('div', {
      fontSize: '9px',
      color: 'rgba(0,255,65,0.45)',
      letterSpacing: '1px',
      textTransform: 'none',
      lineHeight: '1.5',
      whiteSpace: 'pre-line',
      // The hint block documents desktop-only keybindings (WASD / Q-E /
      // Home). On touch devices those are meaningless and it just eats
      // panel real estate, so we hide it entirely on mobile.
      display: mobile ? 'none' : '',
    }, '');
    panel.appendChild(this.infoHintEl);
  }

  private attachCanvasEvents(): void {
    this.canvas.addEventListener('click', (e) => {
      e.stopPropagation();
    });
    this.canvas.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      // Capture on the canvas itself (not `e.target`, which can be a
      // child overlay) so every subsequent move/up for this pointerId
      // is guaranteed to be dispatched here. Without this, pointerup
      // can be lost when the pointer drifts over a sibling element,
      // leaving a phantom entry in `activePointers` that permanently
      // forces the gesture into pinch mode (size>=2 on every new
      // touch → only zoom works, rotation never resumes).
      try {
        this.canvas.setPointerCapture(e.pointerId);
      } catch {
        /* some browsers throw if the pointer was already released */
      }
      this.activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.activePointers.size === 1) {
        this.pointerDown = { x: e.clientX, y: e.clientY, time: performance.now() };
        this.dragging = false;
        this.canvas.style.cursor = 'grabbing';
      } else if (this.activePointers.size === 2) {
        // A second finger just landed — switch from drag-to-rotate to
        // pinch-to-zoom. We snapshot the starting distance and zoom so
        // the zoom tracks the pinch ratio monotonically (pushing apart
        // zooms in from the exact starting level, pinching together
        // zooms out from it). Cancel any in-progress rotation so the
        // camera doesn't lurch when the second finger's motion is
        // interpreted as rotation input.
        this.pointerDown = null;
        this.dragging = false;
        const pts = Array.from(this.activePointers.values());
        const pdx = pts[0]!.x - pts[1]!.x;
        const pdy = pts[0]!.y - pts[1]!.y;
        this.pinchStartDist = Math.max(1, Math.sqrt(pdx * pdx + pdy * pdy));
        this.pinchStartZoom = this.zoom;
      }
    });

    this.canvas.addEventListener('pointermove', (e) => {
      if (!this.activePointers.has(e.pointerId)) return;
      this.activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.activePointers.size >= 2 && this.pinchStartDist > 0) {
        const pts = Array.from(this.activePointers.values());
        const pdx = pts[0]!.x - pts[1]!.x;
        const pdy = pts[0]!.y - pts[1]!.y;
        const dist = Math.max(1, Math.sqrt(pdx * pdx + pdy * pdy));
        // `dist / startDist` is the pinch-spread ratio. Fingers moving
        // *apart* means the user wants a closer view, which in this
        // camera rig is a *smaller* zoom divisor, so we divide rather
        // than multiply.
        const ratio = dist / this.pinchStartDist;
        this.zoom = Math.max(0.3, Math.min(4, this.pinchStartZoom / ratio));
        return;
      }

      if (!this.pointerDown) return;
      const dx = e.clientX - this.pointerDown.x;
      const dy = e.clientY - this.pointerDown.y;
      if (!this.dragging && (dx * dx + dy * dy) > 9) {
        this.dragging = true;
      }
      if (this.dragging) {
        this.rotY += dx * 0.005;
        this.rotX = Math.max(-1.2, Math.min(1.2, this.rotX + dy * 0.005));
        this.pointerDown.x = e.clientX;
        this.pointerDown.y = e.clientY;
      }
    });

    const endPointer = (e: PointerEvent, treatAsTap: boolean): void => {
      const hadEntry = this.activePointers.delete(e.pointerId);
      if (!hadEntry) return;
      if (this.activePointers.size === 1) {
        // Leaving pinch mode but one finger is still down — restart the
        // drag baseline at its current position so we don't treat the
        // residual finger's position-jump-from-last-frame as a huge
        // rotation delta.
        const remaining = Array.from(this.activePointers.values())[0]!;
        this.pointerDown = { x: remaining.x, y: remaining.y, time: performance.now() };
        this.dragging = false;
        this.pinchStartDist = 0;
        return;
      }
      if (this.activePointers.size >= 2) return;
      // No fingers left: finalize drag/tap.
      const wasDragging = this.dragging;
      const wasPinching = this.pinchStartDist > 0;
      this.pointerDown = null;
      this.dragging = false;
      this.pinchStartDist = 0;
      this.canvas.style.cursor = 'grab';
      if (treatAsTap && !wasDragging && !wasPinching) {
        this.handleCanvasClick(e);
      }
    };

    this.canvas.addEventListener('pointerup', (e) => endPointer(e, true));
    this.canvas.addEventListener('pointercancel', (e) => endPointer(e, false));
    // Belt-and-suspenders: if the browser ever revokes our pointer
    // capture (focus shift, panel re-mount, OS-level interruption) we
    // get `lostpointercapture` even when no `pointerup` fires. Treat
    // it the same as a cancel so the pointer is purged from the map
    // and the next touch starts from a clean slate.
    this.canvas.addEventListener('lostpointercapture', (e) =>
      endPointer(e as PointerEvent, false),
    );

    this.canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      const factor = e.deltaY > 0 ? 1.1 : 1 / 1.1;
      this.zoom = Math.max(0.3, Math.min(4, this.zoom * factor));
    }, { passive: false });

    // iOS Safari's default touch gesture for two-finger pinch is
    // page-zoom. The canvas has `touch-action: none` set elsewhere
    // (root container), but some WebKit builds still try to fire
    // `gesturestart/gesturechange/gestureend` — intercept them so the
    // browser doesn't pile on a second zoom layer.
    for (const n of ['gesturestart', 'gesturechange', 'gestureend']) {
      this.canvas.addEventListener(n, (e) => e.preventDefault());
    }
  }

  private handleCanvasClick(e: PointerEvent): void {
    if (!this.renderer) return;
    const rect = this.canvas.getBoundingClientRect();
    this.pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.pointer, this.camera);
    const meshes = Array.from(this.sectorMeshes.values());
    const hits = this.raycaster.intersectObjects(meshes, false);
    if (hits.length === 0) {
      this.selected = null;
      this.updateInfoPanel();
      this.rebuildVolume();
      return;
    }
    const hit = hits[0].object as THREE.Mesh;
    const coord = hit.userData['coord'] as SectorCoord | undefined;
    if (coord) {
      this.selected = { ...coord };
      this.updateInfoPanel();
      this.rebuildVolume();
    }
  }

  private ensureRenderer(): void {
    if (this.renderer) return;
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x000300, 0);

    this.camera = new THREE.PerspectiveCamera(55, 1, 0.1, 1000);
    this.camera.position.set(0, 8, 22);
    this.camera.lookAt(0, 0, 0);

    const ambient = new THREE.AmbientLight(0x335533, 0.7);
    this.scene.add(ambient);
    const dir = new THREE.DirectionalLight(0x88ffaa, 0.8);
    dir.position.set(5, 10, 5);
    this.scene.add(dir);

    this.addGridFloor();
  }

  private addGridFloor(): void {
    const grid = new THREE.GridHelper(
      (VIEW_RADIUS * 2 + 1) * SCALE,
      VIEW_RADIUS * 2 + 1,
      0x22bb55,
      0x114422,
    );
    (grid.material as THREE.Material).transparent = true;
    (grid.material as THREE.Material).opacity = 0.35;
    grid.position.y = -VIEW_RADIUS * SCALE - 0.5;
    this.scene.add(grid);
  }

  private zoneColorNum(diff: number): number {
    switch (diff) {
      case 0: return COLORS.zoneSafeNum;
      case 1: return COLORS.zoneContestedNum;
      case 2: return COLORS.zoneWarNum;
      case 3: return COLORS.zoneVoidNum;
      case 4: return COLORS.zoneAbyssNum;
      case 5: return COLORS.zoneNightmareNum;
      case 6: return COLORS.zoneApexNum;
      case 7: return COLORS.zoneSingularityNum;
      case 8: return COLORS.zoneTranscendentNum;
      default: return COLORS.zoneBeyondNum;
    }
  }

  /**
   * One-line "who owns this sector" string for the info panel.
   *
   * Resolution order:
   *   1. Player claim wins (●) — a real flag planted by another pilot.
   *   2. Otherwise fall back to the deterministic `dominantRace(coord)` so
   *      the panel always shows who lore-holds the sector (◇).
   *   3. Dominant race `null` means frontier — claimable territory (★).
   */
  private ownershipLine(coord: SectorCoord, claim: SectorClaim | undefined): string {
    if (claim?.ownerPlayerId) {
      const race = claim.ownerRace != null ? RACE_INFO[claim.ownerRace] : undefined;
      return `● ${claim.ownerName ?? '?'}${race ? ` · ${race.name}` : ''}`;
    }
    const dom = dominantRace(coord);
    if (dom != null) {
      return `◇ ${t('ownership.dominant', { race: RACE_INFO[dom].name })}`;
    }
    return `★ ${t('ownership.unclaimed')}`;
  }

  /**
   * Owner-race hint color for the sphere emissive.
   *
   * Returns the race's primary tint for NPC/player-owned sectors, or
   * pure white for frontier pockets so the "claimable" hint reads the
   * same regardless of zone. Returns `null` when we have no opinion
   * (unused currently — kept for future ghost/undiscovered rules).
   */
  private ownerTintNum(coord: SectorCoord, claim: SectorClaim | undefined): number {
    if (claim?.ownerRace != null) return RACE_INFO[claim.ownerRace].primaryColor;
    const dom = dominantRace(coord);
    if (dom != null) return RACE_INFO[dom].primaryColor;
    return 0xffffff;
  }

  /** Linear blend of two hex colors (0xRRGGBB), weighted `t` toward `b`. */
  private blendHex(a: number, b: number, tt: number): number {
    const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
    const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
    const r = Math.round(ar + (br - ar) * tt);
    const g = Math.round(ag + (bg - ag) * tt);
    const bl = Math.round(ab + (bb - ab) * tt);
    return (r << 16) | (g << 8) | bl;
  }

  /**
   * Map an event severity onto a hex tint. Picks a hotter color the
   * scarier the event so a glance at the map reflects how worried the
   * pilot should be (info → amber, warning → orange, danger → red).
   */
  private eventRingColorNum(events: ActiveSectorEvent[]): number {
    let worst: ActiveSectorEvent['level'] = 'info';
    const rank: Record<ActiveSectorEvent['level'], number> = {
      info: 0, success: 0, warning: 1, danger: 2,
    };
    for (const ev of events) {
      if (rank[ev.level] > rank[worst]) worst = ev.level;
    }
    switch (worst) {
      case 'danger': return COLORS.eventDangerNum;
      case 'warning': return COLORS.eventWarningNum;
      case 'success': return COLORS.eventSuccessNum;
      default: return COLORS.eventInfoNum;
    }
  }

  private eventRingColorCss(events: ActiveSectorEvent[]): string {
    const n = this.eventRingColorNum(events);
    return `#${n.toString(16).padStart(6, '0')}`;
  }

  /**
   * Format the time remaining on an active event as `Xm Ys` (or
   * `<1m` when very close to expiry). Localized via the i18n bag so
   * the panel keeps reading correctly in Spanish.
   */
  private formatEventCountdown(endsAt: number, now: number): string {
    const ms = endsAt - now;
    if (ms <= 0) return t('sectorEvent.expired');
    if (ms < 60_000) return t('sectorEvent.endsInLessThanMinute');
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const duration = `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
    return t('sectorEvent.endsIn', { duration });
  }

  private zoneColorCss(diff: number): string {
    switch (diff) {
      case 0: return COLORS.zoneSafe;
      case 1: return COLORS.zoneContested;
      case 2: return COLORS.zoneWar;
      case 3: return COLORS.zoneVoid;
      case 4: return COLORS.zoneAbyss;
      case 5: return COLORS.zoneNightmare;
      case 6: return COLORS.zoneApex;
      case 7: return COLORS.zoneSingularity;
      case 8: return COLORS.zoneTranscendent;
      default: return COLORS.zoneBeyond;
    }
  }

  /**
   * Resolve the i18n key for a given zone tier. `diff` is 1-indexed
   * (`getZoneDifficulty` never returns 0); `0` is reserved for the
   * synthetic "safe zone" overlays drawn by the HUD when the pilot is
   * inside a station / warp-gate orbit bubble.
   */
  private zoneTierKey(diff: number): string {
    if (diff <= 0) return 'safe';
    const idx = Math.min(diff - 1, ZONE_TIER_KEYS.length - 1);
    return ZONE_TIER_KEYS[idx]!;
  }

  private zoneLabel(diff: number): string {
    if (diff <= 0) return t('zone.safe');
    return t(`zone.${this.zoneTierKey(diff)}`);
  }

  private zoneDescription(diff: number): string {
    if (diff <= 0) return '';
    return t(`zone.${this.zoneTierKey(diff)}.desc`);
  }

  /**
   * Render a one-line "X hostile / Y neutral" preview for a sector by
   * tallying the deterministic NPC pool. Result is locale-formatted —
   * we elide buckets that come back zero so the panel stays tidy on
   * sectors with only one disposition (e.g. friendly Luminara worlds).
   */
  private formatNpcSummary(summary: SectorNPCSummary): string {
    if (summary.total === 0) return t('npcSummary.empty');
    const parts: string[] = [];
    if (summary.hostile > 0) parts.push(t('npcSummary.hostile', { count: summary.hostile }));
    if (summary.neutral > 0) parts.push(t('npcSummary.neutral', { count: summary.neutral }));
    if (summary.friendly > 0) parts.push(t('npcSummary.friendly', { count: summary.friendly }));
    if (summary.unknown > 0) parts.push(t('npcSummary.unknown', { count: summary.unknown }));
    return parts.join(' · ');
  }

  /**
   * Rebuild the sphere cloud + rings around the current focus point.
   *
   * Performance note: we tear down and recreate every mesh each time
   * the focus moves. The view radius is small (±3 → 343 nodes) and each
   * node uses a shared low-poly sphere, so the cost per rebuild is
   * dominated by material allocation and is imperceptible on anything
   * that can run the game. Doing it this way keeps the data model
   * dumb — no diffing, no mesh pooling — which is worth more than the
   * micro-perf at this scale.
   */
  private rebuildVolume(): void {
    for (const mesh of this.sectorMeshes.values()) {
      this.volumeRoot.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
      for (const c of mesh.children.slice()) {
        mesh.remove(c);
        if ((c as THREE.Mesh).geometry) (c as THREE.Mesh).geometry.dispose();
        if ((c as THREE.Mesh).material) {
          const m = (c as THREE.Mesh).material;
          if (Array.isArray(m)) m.forEach((x) => x.dispose());
          else m.dispose();
        }
      }
    }
    this.sectorMeshes.clear();

    const currentKey = sectorKey(this.current);
    const selectedKey = this.selected ? sectorKey(this.selected) : null;
    const destKey = this.destination ? sectorKey(this.destination) : null;

    for (let dx = -VIEW_RADIUS; dx <= VIEW_RADIUS; dx++) {
      for (let dy = -VIEW_RADIUS; dy <= VIEW_RADIUS; dy++) {
        for (let dz = -VIEW_RADIUS; dz <= VIEW_RADIUS; dz++) {
          const coord: SectorCoord = {
            x: this.focus.x + dx,
            y: this.focus.y + dy,
            z: this.focus.z + dz,
          };
          const key = sectorKey(coord);
          const isCurrent = key === currentKey;
          const isDiscovered = this.discovered.has(key);
          const isSelected = key === selectedKey;
          const isDest = key === destKey;
          const hasGate = sectorHasWarpGate(coord);
          const diff = getZoneDifficulty(coord);
          // Galactic center / origin sector. Promoted to a permanent
          // POI even when undiscovered so a pilot panning around the
          // map always sees where "the middle of the universe" is.
          const isOrigin = coord.x === 0 && coord.y === 0 && coord.z === 0;

          // Undiscovered nodes collapse to a tiny ghost dot so the
          // volume stays readable. Discovered / current / gate / dest
          // / origin sectors get the full sphere so the interesting
          // spots pop.
          const isPoi =
            isCurrent || isDiscovered || hasGate || isSelected || isDest || isOrigin;
          const radius = isPoi ? 0.35 : 0.1;
          const baseColor = isCurrent
            ? COLORS.primaryNum
            : this.zoneColorNum(diff);
          // Subtle race hint: tint the *emissive glow* toward the owning
          // race for POI sectors (discovered/current/dest) so the player
          // has a soft color cue without overriding the zone difficulty
          // reading of the sphere body. Kept off undiscovered ghost dots
          // so the map doesn't leak dominance for sectors you haven't
          // been to yet.
          const showOwnerHint = isDiscovered || isCurrent || isDest;
          const ownerTint = showOwnerHint
            ? this.ownerTintNum(coord, this.claimByKey.get(key))
            : baseColor;
          const emissiveColor = showOwnerHint
            ? this.blendHex(baseColor, ownerTint, 0.3)
            : baseColor;
          const mat = new THREE.MeshStandardMaterial({
            color: baseColor,
            emissive: emissiveColor,
            emissiveIntensity: isSelected ? 1.2 : isCurrent ? 0.9 : isPoi ? 0.5 : 0.12,
            metalness: 0.2,
            roughness: 0.5,
            transparent: true,
            opacity: isPoi ? 0.95 : 0.28,
          });
          const geo = new THREE.SphereGeometry(
            radius,
            isPoi ? 20 : 8,
            isPoi ? 20 : 8,
          );
          const mesh = new THREE.Mesh(geo, mat);
          mesh.position.set(dx * SCALE, dy * SCALE, dz * SCALE);
          mesh.userData['coord'] = coord;
          this.volumeRoot.add(mesh);
          this.sectorMeshes.set(key, mesh);

          // Warp-gate ring — a thin purple halo around gate sectors so
          // the pilot can see warp-enabled destinations at a glance.
          if (hasGate) {
            const ringGeo = new THREE.RingGeometry(0.52, 0.63, 28);
            const ringMat = new THREE.MeshBasicMaterial({
              color: COLORS.warpGateNum,
              transparent: true,
              opacity: 0.5,
              side: THREE.DoubleSide,
              depthWrite: false,
            });
            const ring = new THREE.Mesh(ringGeo, ringMat);
            ring.userData['ringType'] = 'gate';
            mesh.add(ring);
          }

          // Galactic center marker — bright gold double-ring + small
          // octahedron "star" so the origin sector reads as the prime
          // landmark of the universe regardless of zone tint, race
          // ownership, or selection state. The octahedron rotates in
          // the per-frame tick so the beacon feels alive.
          if (isOrigin) {
            const halo = new THREE.Mesh(
              new THREE.RingGeometry(0.42, 0.5, 32),
              new THREE.MeshBasicMaterial({
                color: COLORS.originNum,
                transparent: true,
                opacity: 0.85,
                side: THREE.DoubleSide,
                depthWrite: false,
              }),
            );
            halo.userData['ringType'] = 'origin';
            mesh.add(halo);

            const outer = new THREE.Mesh(
              new THREE.RingGeometry(1.1, 1.28, 36),
              new THREE.MeshBasicMaterial({
                color: COLORS.originNum,
                transparent: true,
                opacity: 0.55,
                side: THREE.DoubleSide,
                depthWrite: false,
              }),
            );
            outer.userData['ringType'] = 'origin';
            mesh.add(outer);

            const star = new THREE.Mesh(
              new THREE.OctahedronGeometry(0.18, 0),
              new THREE.MeshStandardMaterial({
                color: COLORS.originNum,
                emissive: COLORS.originNum,
                emissiveIntensity: 1.4,
                metalness: 0.4,
                roughness: 0.2,
              }),
            );
            star.userData['ringType'] = 'origin-star';
            mesh.add(star);
          }

          // "You are here" ring — bright green pulse so the player
          // can always find themselves in the cloud.
          if (isCurrent) {
            const ringGeo = new THREE.RingGeometry(0.58, 0.72, 32);
            const ringMat = new THREE.MeshBasicMaterial({
              color: COLORS.primaryNum,
              transparent: true,
              opacity: 0.65,
              side: THREE.DoubleSide,
              depthWrite: false,
            });
            const ring = new THREE.Mesh(ringGeo, ringMat);
            ring.userData['ringType'] = 'current';
            mesh.add(ring);
          }

          // Waypoint ring — magenta outline so the destination stands
          // out from warp gates even when it happens to be on one.
          if (isDest) {
            const ringGeo = new THREE.RingGeometry(0.78, 0.95, 32);
            const ringMat = new THREE.MeshBasicMaterial({
              color: COLORS.waypointNum,
              transparent: true,
              opacity: 0.8,
              side: THREE.DoubleSide,
              depthWrite: false,
            });
            const ring = new THREE.Mesh(ringGeo, ringMat);
            ring.userData['ringType'] = 'dest';
            mesh.add(ring);
          }

          // Selection ring — fixed neutral (not zone amber / not race tint).
          if (isSelected) {
            const ringGeo = new THREE.RingGeometry(1.05, 1.22, 32);
            const ringMat = new THREE.MeshBasicMaterial({
              color: COLORS.selectionRingNum,
              transparent: true,
              opacity: 0.92,
              side: THREE.DoubleSide,
              depthWrite: false,
            });
            const ring = new THREE.Mesh(ringGeo, ringMat);
            ring.userData['ringType'] = 'selected';
            mesh.add(ring);
          }

          // Active world event ring — pulses on top of all other rings
          // to advertise that a mothership / raid / skirmish is running
          // here right now. We oversize the radius slightly more than
          // the selection ring so it stays legible even when the
          // selected sector also has an active event.
          const events = this.eventsByKey.get(key);
          if (events && events.length > 0) {
            const colorNum = this.eventRingColorNum(events);
            const ringGeo = new THREE.RingGeometry(1.32, 1.5, 36);
            const ringMat = new THREE.MeshBasicMaterial({
              color: colorNum,
              transparent: true,
              opacity: 0.85,
              side: THREE.DoubleSide,
              depthWrite: false,
            });
            const ring = new THREE.Mesh(ringGeo, ringMat);
            ring.userData['ringType'] = 'event';
            mesh.add(ring);

            // Tiny secondary ring underneath so even a tiny "ghost"
            // dot for an undiscovered sector still gets a visible
            // halo when an event lights it up.
            const innerGeo = new THREE.RingGeometry(0.78, 0.95, 28);
            const innerMat = new THREE.MeshBasicMaterial({
              color: colorNum,
              transparent: true,
              opacity: 0.55,
              side: THREE.DoubleSide,
              depthWrite: false,
            });
            const inner = new THREE.Mesh(innerGeo, innerMat);
            inner.userData['ringType'] = 'event';
            mesh.add(inner);
          }
        }
      }
    }

    // Volume is centred on `focus`, so recenter the group so the focus
    // point sits at the world origin. The camera orbits the origin, so
    // this effectively makes the camera orbit the focus point.
    this.volumeRoot.position.set(0, 0, 0);

    this.rebuildDestinationVisual();
    this.rebuildOriginArrow();
  }

  /**
   * Off-volume direction indicator pointing from the focus center toward
   * the galactic center (0,0,0). Drawn whenever the origin sector is
   * outside the rendered view volume, so a pilot deep in the frontier
   * can still see which way "the middle of the universe" lies relative
   * to where they're currently looking.
   *
   * Built as a chunky cylinder shaft + cone tip + sprite label. The
   * old version used `THREE.Line` which renders as a 1-pixel polyline
   * and was effectively invisible against the dark holo background —
   * this version uses geometry with width and a "0,0,0" billboard so
   * a pilot dozens of sectors out can still spot the beacon at a
   * glance and pan toward it (or hit the gold "⌂ ORIGIN" button to
   * snap straight there).
   */
  private rebuildOriginArrow(): void {
    if (this.originArrow) {
      this.volumeRoot.remove(this.originArrow);
      this.originArrow.geometry.dispose();
      (this.originArrow.material as THREE.Material).dispose();
      this.originArrow = null;
    }
    if (this.originArrowTip) {
      this.volumeRoot.remove(this.originArrowTip);
      this.originArrowTip.geometry.dispose();
      (this.originArrowTip.material as THREE.Material).dispose();
      this.originArrowTip = null;
    }
    if (this.originArrowLabel) {
      this.volumeRoot.remove(this.originArrowLabel);
      const labelMat = this.originArrowLabel.material as THREE.SpriteMaterial;
      if (labelMat.map) labelMat.map.dispose();
      labelMat.dispose();
      this.originArrowLabel = null;
    }

    const originInView =
      Math.abs(this.focus.x) <= VIEW_RADIUS &&
      Math.abs(this.focus.y) <= VIEW_RADIUS &&
      Math.abs(this.focus.z) <= VIEW_RADIUS;
    if (originInView) return;

    const originVec = this.sectorToLocal({ x: 0, y: 0, z: 0 });
    const focusVec = new THREE.Vector3(0, 0, 0);
    const dir = new THREE.Vector3().subVectors(originVec, focusVec).normalize();
    const edge = VIEW_RADIUS * SCALE + 0.5;
    const shaftLength = edge * 1.3;
    const tip = focusVec.clone().add(dir.clone().multiplyScalar(shaftLength));
    const shaftCenter = focusVec.clone().add(dir.clone().multiplyScalar(shaftLength / 2));

    // Cylinder shaft. We orient ConeGeometry / CylinderGeometry by
    // building a quaternion that rotates +Y onto `dir` since both
    // geometries default to pointing up.
    const up = new THREE.Vector3(0, 1, 0);
    const quat = new THREE.Quaternion().setFromUnitVectors(up, dir);
    const shaftGeo = new THREE.CylinderGeometry(0.08, 0.08, shaftLength, 12);
    const shaftMat = new THREE.MeshBasicMaterial({
      color: COLORS.originNum,
      transparent: true,
      opacity: 0.95,
    });
    const shaft = new THREE.Mesh(shaftGeo, shaftMat);
    shaft.position.copy(shaftCenter);
    shaft.quaternion.copy(quat);
    this.volumeRoot.add(shaft);
    this.originArrow = shaft;

    const coneGeo = new THREE.ConeGeometry(0.42, 1.0, 16);
    const coneMat = new THREE.MeshBasicMaterial({
      color: COLORS.originNum,
      transparent: true,
      opacity: 1.0,
    });
    const cone = new THREE.Mesh(coneGeo, coneMat);
    // Anchor the cone so its base sits at `tip` and the tip projects
    // half a cone-length further along `dir`. Keeps the join with the
    // shaft seamless.
    const coneCenter = tip.clone().add(dir.clone().multiplyScalar(0.5));
    cone.position.copy(coneCenter);
    cone.quaternion.copy(quat);
    this.volumeRoot.add(cone);
    this.originArrowTip = cone;

    // Billboard "0,0,0" label past the cone tip so the pilot does not
    // have to read the corner compass to realise the beacon points to
    // the galactic center. Rendered as a sprite (always camera-facing)
    // and sized in world units so it scales sensibly with zoom.
    const labelTexture = this.makeOriginLabelTexture();
    const labelMat = new THREE.SpriteMaterial({
      map: labelTexture,
      transparent: true,
      depthWrite: false,
      depthTest: false,
    });
    const label = new THREE.Sprite(labelMat);
    label.position.copy(tip.clone().add(dir.clone().multiplyScalar(1.6)));
    label.scale.set(2.4, 0.7, 1);
    this.volumeRoot.add(label);
    this.originArrowLabel = label;
  }

  /**
   * Render a small canvas containing the bright gold "0,0,0" tag used
   * on the origin-arrow tip. We bake the text into a texture once per
   * focus rebuild so the GPU doesn't have to rasterise a label every
   * frame, and so we avoid pulling in a Three.js text-geometry helper
   * for a single static badge.
   */
  private makeOriginLabelTexture(): THREE.CanvasTexture {
    const w = 256, h = 96;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, w, h);

    ctx.fillStyle = 'rgba(0,12,0,0.85)';
    ctx.fillRect(2, 2, w - 4, h - 4);
    ctx.strokeStyle = '#ffd700';
    ctx.lineWidth = 4;
    ctx.strokeRect(2, 2, w - 4, h - 4);

    ctx.font = 'bold 56px "Share Tech Mono", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(255,215,0,0.85)';
    ctx.shadowBlur = 14;
    ctx.fillStyle = '#ffd700';
    ctx.fillText(t('universeMap.originLabel'), w / 2, h / 2 + 4);

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
  }

  /**
   * Draw a dashed line from the current sector to the destination if
   * both live in the current view volume, or an arrow from the current
   * sector toward the nearest edge if the destination is out of view.
   *
   * Kept as a separate method so we can refresh it cheaply when the
   * destination changes without rebuilding the whole sphere cloud.
   */
  private rebuildDestinationVisual(): void {
    if (this.destinationLine) {
      this.volumeRoot.remove(this.destinationLine);
      this.destinationLine.geometry.dispose();
      (this.destinationLine.material as THREE.Material).dispose();
      this.destinationLine = null;
    }
    if (this.destinationArrow) {
      this.volumeRoot.remove(this.destinationArrow);
      this.destinationArrow.geometry.dispose();
      (this.destinationArrow.material as THREE.Material).dispose();
      this.destinationArrow = null;
    }
    if (this.destinationArrowTip) {
      this.volumeRoot.remove(this.destinationArrowTip);
      this.destinationArrowTip.geometry.dispose();
      (this.destinationArrowTip.material as THREE.Material).dispose();
      this.destinationArrowTip = null;
    }

    if (!this.destination) return;

    const cur = this.current;
    const dest = this.destination;
    const curVec = this.sectorToLocal(cur);
    const destVec = this.sectorToLocal(dest);

    const destInView =
      Math.abs(dest.x - this.focus.x) <= VIEW_RADIUS &&
      Math.abs(dest.y - this.focus.y) <= VIEW_RADIUS &&
      Math.abs(dest.z - this.focus.z) <= VIEW_RADIUS;

    if (destInView) {
      const geo = new THREE.BufferGeometry().setFromPoints([curVec, destVec]);
      const mat = new THREE.LineDashedMaterial({
        color: COLORS.waypointNum,
        dashSize: 0.35,
        gapSize: 0.22,
        transparent: true,
        opacity: 0.85,
      });
      const line = new THREE.Line(geo, mat);
      line.computeLineDistances();
      this.volumeRoot.add(line);
      this.destinationLine = line;
      return;
    }

    // Off-volume waypoint: shoot an arrow from `current` toward the
    // destination, stopping at the edge of the focus cube so the
    // player can tell which way to pan / fly.
    const dir = new THREE.Vector3().subVectors(destVec, curVec).normalize();
    const edge = VIEW_RADIUS * SCALE + 0.5;
    const tip = curVec.clone().add(dir.clone().multiplyScalar(edge * 1.4));
    const geo = new THREE.BufferGeometry().setFromPoints([curVec, tip]);
    const mat = new THREE.LineBasicMaterial({
      color: COLORS.waypointNum,
      transparent: true,
      opacity: 0.9,
    });
    const line = new THREE.Line(geo, mat);
    this.volumeRoot.add(line);
    this.destinationArrow = line;

    const coneGeo = new THREE.ConeGeometry(0.22, 0.6, 14);
    const coneMat = new THREE.MeshBasicMaterial({
      color: COLORS.waypointNum,
      transparent: true,
      opacity: 0.95,
    });
    const cone = new THREE.Mesh(coneGeo, coneMat);
    cone.position.copy(tip);
    // ConeGeometry points up (+Y); rotate so the tip follows `dir`.
    const up = new THREE.Vector3(0, 1, 0);
    const quat = new THREE.Quaternion().setFromUnitVectors(up, dir);
    cone.quaternion.copy(quat);
    this.volumeRoot.add(cone);
    this.destinationArrowTip = cone;
  }

  /** Map an absolute sector coord to the volume-local coordinate system. */
  private sectorToLocal(s: SectorCoord): THREE.Vector3 {
    return new THREE.Vector3(
      (s.x - this.focus.x) * SCALE,
      (s.y - this.focus.y) * SCALE,
      (s.z - this.focus.z) * SCALE,
    );
  }

  private onFocusChanged(): void {
    this.updateLayerLabel();
    this.updateCompass();
    this.updateInfoPanel();
    this.rebuildVolume();
  }

  private keyToSectorDelta(code: string): SectorCoord | null {
    switch (code) {
      case 'ArrowLeft': case 'KeyA': return { x: -1, y: 0, z: 0 };
      case 'ArrowRight': case 'KeyD': return { x: 1, y: 0, z: 0 };
      case 'ArrowUp': case 'KeyW':
        // Up arrow = "north" = forward (-Z) so the orientation matches
        // the HUD radar and the player's default flight direction.
        return { x: 0, y: 0, z: -1 };
      case 'ArrowDown': case 'KeyS':
        return { x: 0, y: 0, z: 1 };
      default:
        return null;
    }
  }

  private handleKey = (e: KeyboardEvent): void => {
    if (!this.visible) return;
    // While the custom-destination dialog is open the user is most
    // likely typing into one of the X / Y / Z inputs — eating their
    // keystrokes for pan / layer / origin shortcuts here would break
    // both negative numbers (Minus key) and ordinary digits. The
    // dialog wires its own Enter / Escape handlers internally, so
    // stay out of the way until it closes.
    if (this.customDialogEl) return;
    if (e.code === 'Escape' || e.code === 'KeyM') {
      e.preventDefault();
      // Without `stopImmediatePropagation` the same M keypress bubbles
      // out to the global toggle handler in `main.ts`, sees the map is
      // now hidden, and immediately re-opens it — making M look like
      // it doesn't close the map at all. Capture-phase stop kills the
      // sibling listener cleanly.
      e.stopImmediatePropagation();
      this.hide();
      this.onCloseCb();
      return;
    }
    const delta = this.keyToSectorDelta(e.code);
    if (delta) {
      e.preventDefault();
      this.focus = {
        x: this.focus.x + delta.x,
        y: this.focus.y + delta.y,
        z: this.focus.z + delta.z,
      };
      this.onFocusChanged();
      return;
    }
    if (e.code === 'PageUp' || e.code === 'KeyE') {
      e.preventDefault();
      this.focus = { ...this.focus, y: this.focus.y + 1 };
      this.onFocusChanged();
      return;
    }
    if (e.code === 'PageDown' || e.code === 'KeyQ') {
      e.preventDefault();
      this.focus = { ...this.focus, y: this.focus.y - 1 };
      this.onFocusChanged();
      return;
    }
    if (e.code === 'Home') {
      e.preventDefault();
      this.focus = { ...this.current };
      this.onFocusChanged();
      return;
    }
    // `G` snaps focus straight to the galactic center so a far-flung
    // pilot can find (0,0,0) without panning across dozens of
    // sectors. Mirrors the gold "⌂ ORIGIN" button in the layer-control
    // bar; both are documented in the in-panel hint block.
    if (e.code === 'KeyG') {
      e.preventDefault();
      this.focus = { x: 0, y: 0, z: 0 };
      this.selected = { x: 0, y: 0, z: 0 };
      this.onFocusChanged();
      return;
    }
    if (e.code === 'Enter' && this.selected) {
      e.preventDefault();
      this.destination = { ...this.selected };
      this.onSetDestinationCb(this.destination);
      this.rebuildDestinationVisual();
      this.updateInfoPanel();
      this.rebuildVolume();
      return;
    }
    if (e.code === 'Delete' || e.code === 'Backspace') {
      e.preventDefault();
      this.destination = null;
      this.onSetDestinationCb(null);
      this.rebuildDestinationVisual();
      this.updateInfoPanel();
      this.rebuildVolume();
      return;
    }
  };

  private updateLayerLabel(): void {
    const yDiff = this.focus.y - this.current.y;
    const prefix = t('universeMap.layer');
    const suffix = yDiff === 0
      ? ` (${t('universeMap.yours')})`
      : ` (${yDiff > 0 ? '+' : ''}${yDiff})`;
    this.layerEl.textContent =
      `${prefix} ${this.focus.x},${this.focus.y},${this.focus.z}${suffix}`;
  }

  private updateInfoPanel(): void {
    if (this.destination) {
      const d = this.destination;
      const dx = d.x - this.current.x;
      const dy = d.y - this.current.y;
      const dz = d.z - this.current.z;
      const dist = Math.round(Math.sqrt(dx * dx + dy * dy + dz * dz) * 100) / 100;
      this.currentDestEl.textContent =
        `${t('universeMap.activeDestination')}: ${sectorName(d)} [${d.x},${d.y},${d.z}] (${dist} ${t('universeMap.sectorsUnit')})`;
      this.clearBtn.style.display = 'inline-block';
    } else {
      this.currentDestEl.textContent = t('universeMap.noDestination');
      this.clearBtn.style.display = 'none';
    }

    this.infoHintEl.textContent = t('universeMap.hintBody');

    if (!this.selected) {
      this.infoTitleEl.textContent = '—';
      this.infoTitleEl.style.color = 'rgba(0,255,65,0.55)';
      this.infoTitleEl.style.textShadow = 'none';
      this.infoCoordEl.textContent = '';
      this.infoCoordEl.style.color = 'rgba(0,255,65,0.55)';
      this.infoOwnershipEl.textContent = '';
      this.infoDetailEl.textContent = t('universeMap.pickSector');
      this.infoZoneEl.textContent = '';
      this.infoZoneDescEl.textContent = '';
      this.infoNpcSummaryEl.textContent = '';
      this.infoDistanceEl.textContent = '';
      this.infoWarpGateEl.textContent = '';
      this.renderEventInfo([]);
      this.setBtn.disabled = true;
      this.setBtn.style.opacity = '0.35';
      this.setBtn.style.cursor = 'not-allowed';
      return;
    }

    const s = this.selected;
    const key = sectorKey(s);
    const isCurrent = key === sectorKey(this.current);
    const isDiscovered = this.discovered.has(key);
    const hasGate = sectorHasWarpGate(s);
    const diff = getZoneDifficulty(s);
    const isOrigin = s.x === 0 && s.y === 0 && s.z === 0;

    // Sector names are globally shared proper nouns (always English) so
    // we render them raw and put the coords on their own line where
    // they don't visually compete with the name. Origin sector still
    // shows its proper name — the gold "Galactic Center" line below
    // the title flags it as the prime landmark.
    this.infoTitleEl.textContent = sectorName(s);
    this.infoTitleEl.style.color = isOrigin
      ? COLORS.origin
      : isDiscovered || isCurrent ? COLORS.primary : 'rgba(200,220,200,0.8)';
    this.infoTitleEl.style.textShadow = isOrigin
      ? `0 0 8px ${COLORS.origin}, 0 0 16px rgba(255,215,0,0.35)`
      : isDiscovered || isCurrent ? GLOW : 'none';
    this.infoCoordEl.textContent = isOrigin
      ? `[0, 0, 0] · ${t('sector.galacticCenter')}`
      : `[${s.x}, ${s.y}, ${s.z}]`;
    this.infoCoordEl.style.color = isOrigin
      ? COLORS.origin
      : 'rgba(0,255,65,0.55)';

    const claim = this.claimByKey.get(key);
    this.infoOwnershipEl.textContent = this.ownershipLine(s, claim);

    const baseDetail = isCurrent
      ? t('universeMap.youAreHere')
      : isDiscovered
        ? t('universeMap.discovered')
        : t('universeMap.undiscovered');
    this.infoDetailEl.textContent = isOrigin
      ? `${t('sector.galacticCenterHint')}\n${baseDetail}`
      : baseDetail;
    this.infoDetailEl.style.whiteSpace = isOrigin ? 'pre-line' : '';

    this.infoZoneEl.textContent = `${t('universeMap.zone')}: ${this.zoneLabel(diff)}`;
    this.infoZoneEl.style.color = this.zoneColorCss(diff);

    // Tier flavour description — same colour as the zone label but
    // dimmed so it reads as supporting copy. Skipped (empty string)
    // when we're inside a station / warp-gate safe bubble (`diff=0`).
    const tierDesc = this.zoneDescription(diff);
    this.infoZoneDescEl.textContent = tierDesc;
    this.infoZoneDescEl.style.display = tierDesc ? 'block' : 'none';

    // Hostile/neutral breakdown — uses the deterministic procedural
    // generator so the numbers shown match exactly what the server
    // will spawn the moment the pilot warps in.
    const npcSummary = summarizeSectorNPCs(s);
    this.infoNpcSummaryEl.textContent = `${t('npcSummary.title')}: ${this.formatNpcSummary(npcSummary)}`;
    this.infoNpcSummaryEl.style.color = npcSummary.hostile > npcSummary.neutral + npcSummary.friendly
      ? COLORS.danger
      : 'rgba(0,255,65,0.6)';

    const dx = s.x - this.current.x;
    const dy = s.y - this.current.y;
    const dz = s.z - this.current.z;
    const sectorDist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const worldDist = sectorDist * SECTOR_SIZE;
    this.infoDistanceEl.textContent =
      `${t('universeMap.distance')}: ${sectorDist.toFixed(2)} ${t('universeMap.sectorsUnit')} (${Math.round(worldDist).toLocaleString()}u)`;

    this.infoWarpGateEl.textContent = hasGate
      ? `◎  ${t('universeMap.hasWarpGate')}`
      : t('universeMap.noWarpGate');
    this.infoWarpGateEl.style.color = hasGate ? COLORS.warpGate : 'rgba(0,255,65,0.5)';

    this.renderEventInfo(this.eventsByKey.get(key) ?? []);

    if (isCurrent) {
      this.setBtn.disabled = true;
      this.setBtn.style.opacity = '0.35';
      this.setBtn.style.cursor = 'not-allowed';
    } else {
      this.setBtn.disabled = false;
      this.setBtn.style.opacity = '1';
      this.setBtn.style.cursor = 'pointer';
    }
  }

  private resizeRenderer(): void {
    if (!this.renderer) return;
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, rect.width);
    const h = Math.max(1, rect.height);
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  private animate = (): void => {
    if (!this.visible) return;
    this.rafHandle = requestAnimationFrame(this.animate);

    this.time += 0.016;
    this.resizeRenderer();

    const radius = 22 / this.zoom;
    const cosX = Math.cos(this.rotX);
    const sinX = Math.sin(this.rotX);
    const cosY = Math.cos(this.rotY);
    const sinY = Math.sin(this.rotY);
    this.camera.position.set(
      radius * cosX * sinY,
      radius * sinX,
      radius * cosX * cosY,
    );
    this.camera.lookAt(0, 0, 0);

    // Pulse the current and selected nodes so they're always visible
    // even if the camera is far out and most nodes project to a pixel.
    const currentKey = sectorKey(this.current);
    const selectedKey = this.selected ? sectorKey(this.selected) : null;
    const destKey = this.destination ? sectorKey(this.destination) : null;
    for (const [key, mesh] of this.sectorMeshes) {
      if (key === selectedKey) {
        const p = 1 + Math.sin(this.time * 4) * 0.18;
        mesh.scale.setScalar(p);
      } else if (key === currentKey) {
        const p = 1 + Math.sin(this.time * 3) * 0.12;
        mesh.scale.setScalar(p);
      } else if (key === destKey) {
        const p = 1 + Math.sin(this.time * 3) * 0.1;
        mesh.scale.setScalar(p);
      } else {
        mesh.scale.setScalar(1);
      }
      // Keep all rings camera-facing by rotating them around Z. The
      // rings are children of the sphere so a local Z-rotation gives
      // the hologram a subtle "spinning targeting reticle" animation
      // without having to billboard each one.
      for (const c of mesh.children) {
        const kind = c.userData['ringType'];
        if (!kind) continue;
        if (kind === 'origin-star') {
          // Tumble the gold octahedron on multiple axes so it reads as
          // a beacon rather than a flat icon stuck to the sphere.
          c.rotation.x += 0.02;
          c.rotation.y += 0.025;
          c.rotation.z += 0.012;
          continue;
        }
        c.rotation.z +=
          kind === 'selected' ? 0.015 :
          kind === 'event' ? 0.022 :
          kind === 'origin' ? 0.018 :
          0.008;
        if (kind === 'event') {
          const m = (c as THREE.Mesh).material as THREE.MeshBasicMaterial;
          m.opacity = 0.55 + 0.35 * (0.5 + 0.5 * Math.sin(this.time * 3));
        }
        if (kind === 'origin') {
          // Subtle pulse on the gold halo so the beacon never reads
          // as "static decoration".
          const m = (c as THREE.Mesh).material as THREE.MeshBasicMaterial;
          m.opacity = 0.55 + 0.3 * (0.5 + 0.5 * Math.sin(this.time * 2));
        }
      }
    }

    this.tickEventCountdowns();

    this.renderer!.render(this.scene, this.camera);
  };

  /**
   * Refresh the per-event "ends in Xm Ys" labels in the info card so
   * the countdown stays accurate as time passes. We tick once per
   * frame; cheap because there's at most a handful of events on the
   * selected sector.
   */
  private tickEventCountdowns(): void {
    if (this.infoEventEl.style.display === 'none') return;
    const now = Date.now();
    const labels = this.infoEventEl.querySelectorAll<HTMLElement>('[data-event-ends-at]');
    labels.forEach((el) => {
      const ends = Number(el.dataset['eventEndsAt'] ?? 0);
      if (!ends) return;
      el.textContent = this.formatEventCountdown(ends, now);
    });
  }

  show(params: ViewParams): void {
    this.applyState(params);
    // Default focus is the player's current sector on every open so the
    // map always starts "you are here" even if you panned around last
    // time. We also pre-select that sector so the info panel opens
    // populated with the pilot's location instead of an empty "no
    // sector selected" placeholder.
    this.focus = { ...this.current };
    this.selected = { ...this.current };
    // Always start with no in-flight gesture. Defends against the case
    // where the previous session was hidden mid-touch (e.g. the user
    // closed the map with the X button while still pinching) and never
    // got a `pointerup` for one of the fingers.
    this.resetTouchState();
    this.ensureRenderer();
    this.root.style.display = 'flex';
    this.visible = true;
    if (!this.keyListener) {
      this.keyListener = this.handleKey;
      window.addEventListener('keydown', this.keyListener, true);
    }
    this.updateLayerLabel();
    this.updateCompass();
    this.updateInfoPanel();
    this.rebuildVolume();
    this.resizeRenderer();
    this.animate();
  }

  updatePlayerState(params: ViewParams): void {
    this.applyState(params);
    if (!this.visible) return;
    this.updateLayerLabel();
    this.updateInfoPanel();
    this.rebuildVolume();
  }

  private applyState(params: ViewParams): void {
    this.current = { ...params.current };
    this.discovered.clear();
    for (const s of params.discovered) {
      this.discovered.add(sectorKey(s));
    }
    this.discovered.add(sectorKey(this.current));
    this.destination = params.destination ? { ...params.destination } : null;
    this.claimByKey.clear();
    if (params.claims) {
      for (const c of params.claims) this.claimByKey.set(c.sectorKey, c);
    }
    this.eventsByKey.clear();
    if (params.activeEvents) {
      for (const ev of params.activeEvents) {
        const key = sectorKey(ev.sector);
        const list = this.eventsByKey.get(key);
        if (list) list.push(ev);
        else this.eventsByKey.set(key, [ev]);
      }
    }
  }

  /**
   * Populate the active-event card for the currently-selected sector.
   * Renders one block per active event so overlapping events (e.g. a
   * mothership alert AND a skirmish on the same sector) all get
   * surfaced. Hidden when the selected sector has nothing running.
   */
  private renderEventInfo(events: ActiveSectorEvent[]): void {
    while (this.infoEventEl.firstChild) {
      this.infoEventEl.removeChild(this.infoEventEl.firstChild);
    }
    if (events.length === 0) {
      this.infoEventEl.style.display = 'none';
      return;
    }
    this.infoEventEl.style.display = 'flex';
    const tint = this.eventRingColorCss(events);
    this.infoEventEl.style.borderColor = tint;
    this.infoEventEl.style.boxShadow = `0 0 8px ${tint}40`;

    const heading = this.el('div', {
      fontSize: '9px',
      letterSpacing: '3px',
      color: tint,
      textTransform: 'uppercase',
      textShadow: `0 0 4px ${tint}`,
    }, t('sectorEvent.heading'));
    this.infoEventEl.appendChild(heading);

    const now = Date.now();
    for (const ev of events) {
      const card = this.el('div', {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        marginTop: '2px',
      });
      const title = this.el('div', {
        fontSize: '12px',
        fontWeight: 'bold',
        letterSpacing: '1px',
        color: tint,
        textShadow: `0 0 4px ${tint}`,
      }, ev.title);
      card.appendChild(title);

      const body = this.el('div', {
        fontSize: '10px',
        color: 'rgba(255,255,255,0.8)',
        lineHeight: '1.4',
      }, ev.body);
      card.appendChild(body);

      const countdown = this.el('div', {
        fontSize: '10px',
        color: 'rgba(255,255,255,0.55)',
      }, this.formatEventCountdown(ev.endsAt, now));
      countdown.dataset['eventEndsAt'] = String(ev.endsAt);
      card.appendChild(countdown);
      this.infoEventEl.appendChild(card);
    }
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    cancelAnimationFrame(this.rafHandle);
    this.root.style.display = 'none';
    if (this.keyListener) {
      window.removeEventListener('keydown', this.keyListener, true);
      this.keyListener = null;
    }
    // Drop the custom-destination overlay if it was still up — without
    // this it stays parented to the (now display:none) root and pops
    // back into view the next time the map is opened with stale input
    // values.
    this.closeCustomDestinationDialog();
    // Hiding the canvas (display:none) does NOT fire pointerup/cancel
    // for any in-flight touches, so we have to purge them ourselves.
    // Without this, reopening the map keeps the stale finger entries
    // in `activePointers` and the very first touch enters pinch mode
    // (size>=2) — that's what makes rotation appear to "stop working
    // even after closing and reopening the window".
    this.resetTouchState();
  }

  private resetTouchState(): void {
    for (const id of this.activePointers.keys()) {
      try {
        this.canvas.releasePointerCapture(id);
      } catch {
        /* never had capture / already released */
      }
    }
    this.activePointers.clear();
    this.pointerDown = null;
    this.dragging = false;
    this.pinchStartDist = 0;
    this.pinchStartZoom = this.zoom;
    this.canvas.style.cursor = 'grab';
  }

  isVisible(): boolean {
    return this.visible;
  }

  dispose(): void {
    this.hide();
    for (const mesh of this.sectorMeshes.values()) {
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    this.sectorMeshes.clear();
    if (this.destinationLine) {
      this.destinationLine.geometry.dispose();
      (this.destinationLine.material as THREE.Material).dispose();
    }
    if (this.destinationArrow) {
      this.destinationArrow.geometry.dispose();
      (this.destinationArrow.material as THREE.Material).dispose();
    }
    if (this.destinationArrowTip) {
      this.destinationArrowTip.geometry.dispose();
      (this.destinationArrowTip.material as THREE.Material).dispose();
    }
    if (this.originArrow) {
      this.originArrow.geometry.dispose();
      (this.originArrow.material as THREE.Material).dispose();
    }
    if (this.originArrowTip) {
      this.originArrowTip.geometry.dispose();
      (this.originArrowTip.material as THREE.Material).dispose();
    }
    if (this.originArrowLabel) {
      const labelMat = this.originArrowLabel.material as THREE.SpriteMaterial;
      if (labelMat.map) labelMat.map.dispose();
      labelMat.dispose();
    }
    this.renderer?.dispose();
    this.renderer = null;
    this.root.remove();
  }
}
